import { createContext, useContext, useMemo } from 'react'
import type { ReactNode } from 'react'

import { initLogger } from '@/lib/logger.ts'
import type { SyncManager } from '@/lib/sync-manager/dexie-sync-manager.ts'
import { useDexieDb } from '@/db/dexie-context.tsx'
import { createSyncManager } from '@/db/sync-manager-instance.ts'
import { useAuth } from '@/features/auth/hooks/use-auth.ts'

const logger = initLogger('sync-manager-context')

interface SyncManagerContextValue {
  manager: SyncManager | null
  isReady: boolean
}

const SyncManagerContext = createContext<SyncManagerContextValue | null>(null)

interface SyncManagerProviderProps {
  children: ReactNode
}

export function SyncManagerProvider({ children }: SyncManagerProviderProps) {
  const auth = useAuth()
  const db = useDexieDb({ safe: true })

  // Create singleton sync manager instance
  const manager = useMemo(() => {
    if (!db) {
      logger.debug('No Dexie DB available yet')
      return null
    }

    const syncManager = createSyncManager(db)
    logger.info('Created SyncManager singleton')
    return syncManager
  }, [db])

  // Ready when we have manager and authenticated user
  const isReady = !!manager && !!auth.user?.id && !auth.loading

  const value = useMemo(
    () => ({
      manager,
      isReady,
    }),
    [manager, isReady]
  )

  return (
    <SyncManagerContext.Provider value={value}>
      {children}
    </SyncManagerContext.Provider>
  )
}

export function useSyncManagerContext(): SyncManagerContextValue {
  const context = useContext(SyncManagerContext)
  if (!context) {
    throw new Error(
      'useSyncManagerContext must be used within SyncManagerProvider'
    )
  }
  return context
}
